import {
  importCatalogListPage,
  importCatalogPage,
  importHomePage,
  importOrdersPage,
  importProcurementPage,
} from './lazyPages';

/** Path → dynamic import (must match `App.tsx` lazy page chunks). */
const ROUTE_CHUNKS: { prefix: string; load: () => Promise<unknown> }[] = [
  { prefix: '/settings/fulfillment', load: () => import('../pages/FulfillmentSettingsPage') },
  {
    prefix: '/settings/data-sources',
    load: () => import('../pages/OpenSettingsDataSources'),
  },
  { prefix: '/crm/flows/', load: () => import('../pages/LeadFlowDetailPage') },
  { prefix: '/crm/flows', load: () => import('../pages/LeadFlowsPage') },
  { prefix: '/roles/', load: () => import('../pages/RoleDetailPage') },
  { prefix: '/roles', load: () => import('../pages/RolesPage') },
  { prefix: '/users', load: () => import('../pages/UsersPage') },
  { prefix: '/search', load: () => import('../pages/SearchPage') },
  { prefix: '/documents', load: () => import('../pages/DocumentsPage') },
  { prefix: '/b2b', load: () => import('../pages/B2bLegacyRedirect') },
  { prefix: '/customer', load: () => import('../pages/CustomerPage') },
  { prefix: '/procurement/quotes', load: () => import('../pages/QuotesPage') },
  { prefix: '/procurement', load: importProcurementPage },
  {
    prefix: '/conventions/templates/',
    load: () => import('../pages/conventions/ConventionTemplateEditorPage'),
  },
  {
    prefix: '/conventions/templates',
    load: () => import('../pages/conventions/ConventionTemplatesPage'),
  },
  { prefix: '/conventions/', load: () => import('../pages/conventions/ConventionDetailPage') },
  { prefix: '/conventions', load: () => import('../pages/ConventionsPage') },
  { prefix: '/sales/quotes', load: () => import('../pages/SaleQuotesPage') },
  { prefix: '/orders', load: importOrdersPage },
  { prefix: '/catalog/option-groups/', load: () => import('../pages/OptionGroupDetailPage') },
  { prefix: '/catalog/types/', load: () => import('../pages/ProductTypeDetailPage') },
  { prefix: '/catalog/types', load: () => import('../pages/ProductTypesPage') },
  { prefix: '/catalog/products', load: importCatalogPage },
  { prefix: '/catalog', load: importCatalogListPage },
  { prefix: '/inventory/locations', load: () => import('../pages/LocationsPage') },
  { prefix: '/inventory/lots/', load: () => import('../pages/InventoryLotDetailPage') },
  { prefix: '/inventory/lots', load: () => import('../pages/InventoryLotsPage') },
  { prefix: '/inventory', load: () => import('../pages/InventoryPage') },
  { prefix: '/leads', load: () => import('../pages/LeadsPage') },
  { prefix: '/support', load: () => import('../pages/SupportTicketsPage') },
  { prefix: '/accounting', load: () => import('../pages/AccountingPage') },
  { prefix: '/storage', load: () => import('../pages/StoragePage') },
  {
    prefix: '/playground/convention-letterhead',
    load: () => import('../pages/ConventionLetterheadPlaygroundPage'),
  },
  { prefix: '/playground', load: () => import('../pages/DesignSystemPlaygroundPage') },
  { prefix: '/', load: importHomePage },
];

const inflight = new Map<string, Promise<unknown>>();
const warmed = new Set<string>();

function normalizePath(pathname: string): string {
  return pathname.split('?')[0]?.split('#')[0] ?? '/';
}

function prefixMatches(path: string, prefix: string): boolean {
  if (prefix.endsWith('/')) {
    return path.startsWith(prefix);
  }
  return path === prefix || path.startsWith(`${prefix}/`);
}

function matchChunk(pathname: string) {
  const path = normalizePath(pathname);
  for (const entry of ROUTE_CHUNKS) {
    if (entry.prefix === '/') {
      if (path === '/') return entry;
      continue;
    }
    if (prefixMatches(path, entry.prefix)) return entry;
  }
  return ROUTE_CHUNKS.find((e) => e.prefix === '/') ?? null;
}

/** Suspense/outlet key: one instance per lazy chunk (not per detail sub-route). */
export function routeChunkKey(pathname: string): string {
  return matchChunk(pathname)?.prefix ?? '/';
}

/**
 * Warm route chunk — fire-and-forget, deduped. Same `import()` path as `React.lazy` in `App.tsx`
 * so Vite serves one chunk (module cache after first resolve).
 */
export function prefetchRoute(pathname: string): void {
  const entry = matchChunk(pathname);
  if (!entry) return;
  const key = entry.prefix;
  if (warmed.has(key) || inflight.has(key)) return;
  const task = entry
    .load()
    .then((mod) => {
      warmed.add(key);
      return mod;
    })
    .finally(() => {
      if (inflight.get(key) === task) inflight.delete(key);
    });
  inflight.set(key, task);
}

/** Prefetch several destinations (e.g. all items in an open nav cluster). */
export function prefetchRoutes(paths: readonly string[]): void {
  for (const path of paths) prefetchRoute(path);
}

/** No bulk prefetch on login — let navigation / hover drive lazy loads. */
export function prefetchCommonAdminRoutes(): void {
  /* intentionally empty */
}
